import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { fmtMoney } from '../../lib/currency';
import { useUiStore, toDateStr } from '../../lib/uiStore';
import { fmtTimeRange, minutesToLabel } from '../../lib/time';
import { useLang } from '../../lib/lang';
import { useStatusName } from '../../lib/localized';
import type { Dashboard } from '../../lib/types';

type Preset = 'today' | 'week' | 'month';

function rangeFor(preset: Preset): { from: string; to: string; label: string } {
  const now = new Date();
  const today = toDateStr(now);
  if (preset === 'today') return { from: today, to: today, label: 'Today' };
  if (preset === 'week') {
    const from = new Date(now); from.setDate(from.getDate() - 6);
    return { from: toDateStr(from), to: today, label: 'Last 7 days' };
  }
  const from = new Date(now.getFullYear(), now.getMonth(), 1);
  return { from: toDateStr(from), to: today, label: 'This month' };
}

const PRESET_LABEL: Record<Preset, { en: string; ar: string }> = {
  today: { en: 'Today', ar: 'اليوم' },
  week: { en: '7 days', ar: '7 أيام' },
  month: { en: 'month', ar: 'شهر' },
};

export function DashboardView({ branchId }: { branchId: number }): JSX.Element {
  const { t } = useLang();
  const [preset, setPreset] = useState<Preset>('today');
  const range = useMemo(() => rangeFor(preset), [preset]);
  const openEdit = useUiStore((s) => s.openEdit);

  const q = useQuery({
    queryKey: ['dashboard', branchId, range.from, range.to],
    queryFn: () => api.dashboard(branchId, { from: range.from, to: range.to }),
  });

  return (
    <div className="appt-scroll h-full overflow-auto p-3 sm:p-4">
      <div className="mx-auto max-w-6xl">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2 sm:mb-4">
          <h1 className="text-lg font-bold text-slate-800 sm:text-xl dark:text-slate-100">{t({ en: 'Dashboard', ar: 'لوحة التحكم' })}</h1>
          <div className="appt-seg">
            {(['today', 'week', 'month'] as const).map((p) => (
              <button key={p} aria-pressed={preset === p} onClick={() => setPreset(p)} className="capitalize">
                {t(PRESET_LABEL[p])}
              </button>
            ))}
          </div>
        </div>

        {q.isLoading && <p className="py-10 text-center text-sm text-slate-400">{t({ en: 'Loading…', ar: 'جارٍ التحميل…' })}</p>}
        {q.data && <DashboardBody d={q.data} onOpen={openEdit} />}
      </div>
    </div>
  );
}

function DashboardBody({ d, onOpen }: { d: Dashboard; onOpen: (id: number) => void }): JSX.Element {
  const { t: tr } = useLang();
  const statusName = useStatusName();
  const t = d.totals;
  const maxHour = Math.max(1, ...d.busiest_hours.map((h) => h.count));
  const maxStatus = Math.max(1, ...d.by_status.map((s) => s.count));
  const appts = (n: number): string => tr({ en: `${n} appts`, ar: `${n} مواعيد` });

  return (
    <div className="space-y-3 sm:space-y-4">
      {/* KPI tiles */}
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 sm:gap-3">
        <Kpi label={tr({ en: 'Appointments', ar: 'المواعيد' })} value={t.total} />
        <Kpi label={tr({ en: 'Revenue', ar: 'الإيرادات' })} value={fmtMoney(t.revenue)} accent="#16a34a" />
        <Kpi label={tr({ en: 'Occupancy', ar: 'الإشغال' })} value={`${t.occupancy}%`} accent="#6366f1" />
        <Kpi label={tr({ en: 'Upcoming', ar: 'القادمة' })} value={t.upcoming} accent="#0ea5e9" />
        <Kpi label={tr({ en: 'Completed', ar: 'مكتمل' })} value={`${t.completion_rate}%`} sub={appts(t.completed)} accent="#16a34a" />
        <Kpi label={tr({ en: 'Cancelled', ar: 'ملغي' })} value={`${t.cancellation_rate}%`} sub={appts(t.cancelled)} accent="#ef4444" />
        <Kpi label={tr({ en: 'No-show', ar: 'عدم حضور' })} value={`${t.no_show_rate}%`} sub={appts(t.no_show)} accent="#f97316" />
        <Kpi label={tr({ en: 'Avg duration', ar: 'متوسط المدة' })} value={`${Math.round(t.avg_duration)} ${tr({ en: 'min', ar: 'دقيقة' })}`} sub={tr({ en: `${t.converted} converted`, ar: `${t.converted} محوّلة` })} accent="#64748b" />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        {/* Busiest hours */}
        <Card title={tr({ en: 'Busiest hours', ar: 'أكثر الساعات ازدحاماً' })}>
          {d.busiest_hours.length === 0 ? <Empty /> : (
            <div className="flex items-end gap-1" style={{ height: 140 }}>
              {d.busiest_hours.map((h) => (
                <div key={h.hour} className="flex flex-1 flex-col items-center justify-end gap-1" title={`${h.count} at ${minutesToLabel(h.hour * 60)}`}>
                  <div className="w-full rounded-t bg-brand" style={{ height: `${(h.count / maxHour) * 100}%`, minHeight: 3 }} />
                  <span className="text-[9px] text-slate-400">{h.hour}</span>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Status breakdown */}
        <Card title={tr({ en: 'By status', ar: 'حسب الحالة' })}>
          {d.by_status.length === 0 ? <Empty /> : (
            <div className="space-y-2">
              {d.by_status.map((s) => (
                <div key={s.status_id} className="flex items-center gap-2">
                  <span className="w-24 truncate text-xs font-medium text-slate-600 dark:text-slate-300">{statusName(s.status_id, s.status_name) || '—'}</span>
                  <div className="h-4 flex-1 overflow-hidden rounded bg-slate-100 dark:bg-slate-800">
                    <div className="h-full rounded" style={{ width: `${(s.count / maxStatus) * 100}%`, background: s.color ?? '#3B82F6' }} />
                  </div>
                  <span className="w-8 text-right text-xs font-semibold text-slate-500">{s.count}</span>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Top services */}
        <Card title={tr({ en: 'Top services', ar: 'أفضل الخدمات' })}>
          <RankList rows={d.top_services.map((s) => ({ key: s.item_id, name: s.name ?? `#${s.item_id}`, count: s.count, extra: fmtMoney(s.revenue) }))} />
        </Card>

        {/* Top employees */}
        <Card title={tr({ en: 'Top employees', ar: 'أفضل الموظفين' })}>
          <RankList rows={d.top_employees.map((e) => ({ key: e.employee_id ?? 0, name: e.name ?? tr({ en: 'Unassigned', ar: 'غير معيّن' }), count: e.count, extra: fmtMoney(e.revenue) }))} />
        </Card>
      </div>

      {/* Upcoming */}
      <Card title={tr({ en: 'Upcoming appointments', ar: 'المواعيد القادمة' })}>
        {d.upcoming.length === 0 ? <Empty /> : (
          <div className="divide-y divide-slate-100 dark:divide-slate-800">
            {d.upcoming.map((s) => (
              <button key={s.id} onClick={() => onOpen(s.id)} className="flex w-full items-center justify-between gap-2 py-2 text-left hover:bg-slate-50 dark:hover:bg-slate-800/50">
                <span className="flex min-w-0 items-center gap-2">
                  <span className="h-2 w-2 flex-none rounded-full" style={{ background: s.color ?? s.status_color ?? '#3B82F6' }} />
                  <span className="truncate text-sm font-medium text-slate-800 dark:text-slate-100">{s.customer_name ?? tr({ en: 'Walk-in', ar: 'زائر' })}</span>
                </span>
                <span className="flex-none text-end text-xs text-slate-400">
                  {fmtTimeRange(s.start_at, s.end_at)}
                  <span className="hidden sm:inline"> · {statusName(s.status_id, s.status_name)}</span>
                </span>
              </button>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

function Kpi({ label, value, sub, accent }: { label: string; value: string | number; sub?: string; accent?: string }): JSX.Element {
  return (
    <div className="appt-card p-3 sm:p-4">
      <div className="truncate text-[10px] font-semibold uppercase tracking-wide text-slate-400 sm:text-xs">{label}</div>
      <div className="mt-1 text-xl font-bold sm:text-2xl" style={{ color: accent ?? undefined }}>{value}</div>
      {sub && <div className="truncate text-[11px] text-slate-400">{sub}</div>}
    </div>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }): JSX.Element {
  return (
    <div className="appt-card p-3 sm:p-4">
      <h3 className="mb-3 text-sm font-bold text-slate-700 dark:text-slate-200">{title}</h3>
      {children}
    </div>
  );
}

function RankList({ rows }: { rows: Array<{ key: number; name: string; count: number; extra: string }> }): JSX.Element {
  if (rows.length === 0) return <Empty />;
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <div className="space-y-2">
      {rows.map((r) => (
        /* Four fixed columns left the bar about 70px wide on a phone, so the
           label narrows and the revenue figure drops below sm — the bar is the
           point of the row and it keeps the space. */
        <div key={r.key} className="flex items-center gap-2">
          <span className="w-20 truncate text-xs font-medium text-slate-600 sm:w-28 dark:text-slate-300">{r.name}</span>
          <div className="h-4 flex-1 overflow-hidden rounded bg-slate-100 dark:bg-slate-800">
            <div className="h-full rounded bg-brand/70" style={{ width: `${(r.count / max) * 100}%` }} />
          </div>
          <span className="w-6 text-right text-xs font-semibold text-slate-500 sm:w-8">{r.count}</span>
          <span className="hidden w-14 text-right text-[11px] text-slate-400 sm:block">{r.extra}</span>
        </div>
      ))}
    </div>
  );
}

function Empty(): JSX.Element {
  const { t } = useLang();
  return <p className="py-4 text-center text-xs text-slate-400">{t({ en: 'No data for this period.', ar: 'لا توجد بيانات لهذه الفترة.' })}</p>;
}
